import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Notification from "../components/common/Notification";
import styles from "./AdminPage.module.css";

// --- FIX: Define your backend URL to ensure images load from the correct source
const BASE_URL = "https://your-live-api-url.com";

function AdminProductEditPage() {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = location.pathname === "/admin/products/new";

  const [product, setProduct] = useState({
    id: "",
    name: "",
    price: "",
    category: "",
    description: "",
    minQuantity: 1,
    maxQuantity: 10,
  });

  // --- NEW: Variations State for Sizes (250g, 500g, etc.)
  const [variations, setVariations] = useState([]);

  const [categories, setCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [notification, setNotification] = useState(null);
  const showToast = (message, type = "success") => {
    setNotification({ message, type });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadPageData = async () => {
      setIsLoading(true);

      try {
        const categoriesRes = await fetch("/api/categories?type=Product");
        if (!categoriesRes.ok) throw new Error("Failed to load categories.");
        const categoriesData = await categoriesRes.json();
        setCategories(categoriesData);

        let defaultCategory = "";
        if (categoriesData.length > 0) {
          defaultCategory = categoriesData[0]._id;
        }

        if (!isNew) {
          const productRes = await fetch(`/api/cms/products/${productId}`);
          if (!productRes.ok) throw new Error("Product not found.");

          const productData = await productRes.json();
          const priceInfo = [
            `${productData.price || 0}Br`,
            productData.description || "",
          ].join("\n");

          setProduct({
            id: productData.productID,
            name: productData.name,
            price: priceInfo,
            category: productData.category
              ? productData.category._id
              : defaultCategory,
            description: productData.description,
            minQuantity: productData.minQuantity || 1,
            maxQuantity: productData.maxQuantity || 10,
          });
          setExistingImageUrl(productData.imageURL);
          // Load variations from DB
          setVariations(productData.variations || []);
        } else {
          setProduct((prev) => ({
            ...prev,
            category: defaultCategory,
            minQuantity: 1,
            maxQuantity: 10,
          }));
        }
      } catch (err) {
        console.error("Error loading page data:", err);
        showToast(err.message, "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadPageData();
  }, [productId, isNew]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
  };

  // --- Variation Handlers ---
  const addVariation = () =>
    setVariations([...variations, { weight: "", price: "" }]);
  const removeVariation = (index) =>
    setVariations(variations.filter((_, i) => i !== index));
  const handleVariationChange = (index, field, value) => {
    const newVars = [...variations];
    newVars[index][field] = value;
    setVariations(newVars);
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (Number(product.minQuantity) > Number(product.maxQuantity)) {
      showToast("Minimum quantity cannot be greater than Maximum.", "error");
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    const [priceLine, ...descLines] = product.price.split("\n");
    const priceValue = parseFloat(priceLine.replace(/[^0-9.]/g, "") || 0);
    const description = descLines.join("\n");

    formData.append("productID", product.id);
    formData.append("name", product.name);
    formData.append("category", product.category);
    formData.append("price", priceValue);
    formData.append("description", description);
    formData.append("minQuantity", product.minQuantity);
    formData.append("maxQuantity", product.maxQuantity);

    // Send variations as a JSON string
    formData.append("variations", JSON.stringify(variations));

    if (imageFile) {
      formData.append("image", imageFile);
    }

    const url = isNew ? "/api/cms/products" : `/api/cms/products/${productId}`;
    const method = isNew ? "POST" : "PUT";
    const adminKey = localStorage.getItem("adminKey");

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "x-admin-key": adminKey,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        showToast(
          `Successfully ${isNew ? "created" : "updated"} product!`,
          "success",
        );
        setTimeout(() => {
          navigate("/admin/products");
        }, 1500);
      } else {
        showToast(`Failed: ${data.message || "Not authorized"}`, "error");
      }
    } catch (error) {
      console.error("API request failed:", error);
      showToast("Network Error: Could not connect to backend server.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.adminPage}>
      <Navbar />

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className={styles.adminHero}>
        <h1>{isNew ? "Add New Product" : "Edit Product"}</h1>
      </div>

      <div className={styles.adminContentContainer}>
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <form className={styles.adminEditForm} onSubmit={handleSubmit}>
            <div className={styles.formRowGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="id">Custom ID (SKU) *</label>
                <input
                  type="text"
                  id="id"
                  name="id"
                  value={product.id || ""}
                  onChange={handleInputChange}
                  required
                  disabled={!isNew}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="minQuantity">Min Order Quantity</label>
                <input
                  type="number"
                  id="minQuantity"
                  name="minQuantity"
                  value={product.minQuantity}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="maxQuantity">Max Order Quantity</label>
                <input
                  type="number"
                  id="maxQuantity"
                  name="maxQuantity"
                  value={product.maxQuantity}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="name">Product Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={product.name || ""}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={product.category || ""}
                onChange={handleInputChange}
                disabled={categories.length === 0}
              >
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* --- NEW: VARIATIONS SECTION --- */}
            <div
              className={styles.formGroup}
              style={{
                border: "1px solid #333",
                padding: "20px",
                borderRadius: "4px",
              }}
            >
              <label style={{ color: "#00e5ff", fontWeight: "bold" }}>
                Product Variations (Sizes/Weights)
              </label>
              {variations.map((v, index) => (
                <div
                  key={index}
                  style={{ display: "flex", gap: "10px", marginTop: "10px" }}
                >
                  <input
                    placeholder="Weight (e.g. 500g)"
                    value={v.weight}
                    onChange={(e) =>
                      handleVariationChange(index, "weight", e.target.value)
                    }
                    style={{ flex: 2 }}
                  />
                  <input
                    type="number"
                    placeholder="Price (Br)"
                    value={v.price}
                    onChange={(e) =>
                      handleVariationChange(index, "price", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeVariation(index)}
                    className={`${styles.adminBtn} ${styles.danger}`}
                    style={{ padding: "5px 15px" }}
                  >
                    Delete
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addVariation}
                className={styles.adminBtn}
                style={{ marginTop: "15px", fontSize: "0.8rem" }}
              >
                + Add Size Option
              </button>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="price">
                Price (e.g., 7800Br) / Info (use \n for new lines)
              </label>
              <textarea
                id="price"
                name="price"
                rows="4"
                placeholder="e.g., 7800Br\nOrigin: Sidama\nProcess: Natural"
                value={product.price || ""}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="image">
                Product Image {isNew ? "*" : "(Optional: Upload to replace)"}
              </label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileChange}
              />
              {!isNew && existingImageUrl && (
                <div style={{ marginTop: "10px" }}>
                  <p className={styles.formHint}>Current Image:</p>
                  <img
                    /* FIX: Ensure image loads from your live URL if the path is relative */
                    src={
                      existingImageUrl.startsWith("http")
                        ? existingImageUrl
                        : `${BASE_URL}${existingImageUrl}`
                    }
                    alt="Existing product"
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <Link
                to="/admin/products"
                className={`${styles.adminBtn} ${styles.danger}`}
              >
                Cancel
              </Link>
              <button
                type="submit"
                className={`${styles.adminBtn} ${styles.primary}`}
                disabled={isLoading || categories.length === 0}
              >
                {isLoading
                  ? "Saving..."
                  : isNew
                    ? "Create Product"
                    : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default AdminProductEditPage;
